import { Button } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import {
  format,
  isAfter,
  isBefore,
  isToday,
  startOfDay,
  differenceInDays,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import React from "react";
import type { DateRange as CalendarRange } from "react-day-picker";
import { cn } from "../../lib/utils";

interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  date?: DateRange;
  onDateChange?: (range: DateRange | undefined) => void;
  maxDays?: number;
  allowUnlimited?: boolean;
  allowFutureDates?: boolean; 
  /** Sobrescreve o estilo do botão (ex.: tamanho compacto), preservando o tema. */
  buttonClassName?: string;
  /** Texto mostrado dentro do botão quando nenhum período está selecionado. */
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
}

function DateRangePicker({
  className,
  date: initialDate,
  onDateChange,
  maxDays = 7,
  allowUnlimited = false,
  allowFutureDates = false,
  buttonClassName,
  placeholder = "Selecione um período",
  minDate,
  maxDate,
  ...props
}: DateRangePickerProps) {
  const [applied, setApplied] = React.useState<DateRange | undefined>(
    initialDate
  );
  const [draft, setDraft] = React.useState<CalendarRange | undefined>(
    initialDate
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const nextClickStartsNewRange = React.useRef<boolean>(true);

  const closeAfterCurrentRender = (): void => {
    window.setTimeout(() => setIsOpen(false), 0);
  };

  const clampToMaxDays = (from: Date, to: Date): Date => {
    if (allowUnlimited) return to;
    if (differenceInDays(to, from) <= maxDays) return to;

    const clamped: Date = new Date(from);
    clamped.setDate(from.getDate() + maxDays);
    return clamped;
  };

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);
    if (open) {
      setDraft(applied);
      nextClickStartsNewRange.current = true;
    }
  };

  const handleSelect = (
    _range: CalendarRange | undefined,
    selectedDay: Date
  ): void => {
    if (!selectedDay) return;

    if (nextClickStartsNewRange.current) {
      nextClickStartsNewRange.current = false;
      setDraft({ from: selectedDay, to: undefined });
      return;
    }

    const rangeStart: Date = draft?.from ?? selectedDay;
    const [from, to]: [Date, Date] = isAfter(rangeStart, selectedDay)
      ? [selectedDay, rangeStart]
      : [rangeStart, selectedDay];

    const nextRange: DateRange = { from, to: clampToMaxDays(from, to) };
    setApplied(nextRange);
    setDraft(nextRange);
    onDateChange?.(nextRange);
    nextClickStartsNewRange.current = true;
    closeAfterCurrentRender();
  };

  const isFutureOrToday = (date: Date) => {
    if (allowFutureDates) return false;
    const today = startOfDay(new Date());
    const checkDate = startOfDay(date);
    return isToday(checkDate) || isAfter(checkDate, today);
  };

  const isOutsideBounds = (date: Date): boolean => {
    const checkDate: Date = startOfDay(date);
    if (minDate && isBefore(checkDate, startOfDay(minDate))) return true;
    return Boolean(maxDate && isAfter(checkDate, startOfDay(maxDate)));
  };

  const isDisabled = (date: Date): boolean => isFutureOrToday(date) || isOutsideBounds(date);

  return (
    <div className={cn("grid gap-2", className)} {...props}>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            className={cn(
              "w-[300px] justify-start text-left font-normal bg-[#2E0D1E] hover:bg-[#2E0D1E]/90 text-white",
              buttonClassName
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {applied?.from ? (
              <>
                {format(applied.from, "dd 'de' MMM, yyyy", { locale: ptBR })} -{" "}
                {format(applied.to, "dd 'de' MMM, yyyy", { locale: ptBR })}
              </>
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          side="bottom"
          avoidCollisions
          collisionPadding={16}
          sticky="always"
        >
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={applied?.from ?? minDate}
            selected={draft}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={isDisabled}
            locale={ptBR}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default DateRangePicker;
